import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, PieChart, Pie, Cell,
} from "recharts";
import { Download, FileSpreadsheet, Star, TrendingUp, RefreshCw, Filter, Building2, Users, ClipboardCheck, FileText, ShieldCheck } from "lucide-react";

const PALETTE = ["#2C7BB6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#84CC16"];

function KPICard({ label, value, sub, testId, tone = "slate", icon: Icon }) {
  const tones = {
    primary: { border: "border-sky-200 hover:border-sky-600", iconBg: "bg-sky-100 text-sky-700", accent: "text-sky-900" },
    emerald: { border: "border-emerald-200 hover:border-emerald-600", iconBg: "bg-emerald-100 text-emerald-700", accent: "text-emerald-900" },
    violet: { border: "border-violet-200 hover:border-violet-600", iconBg: "bg-violet-100 text-violet-700", accent: "text-violet-900" },
    amber: { border: "border-amber-200 hover:border-amber-600", iconBg: "bg-amber-100 text-amber-800", accent: "text-amber-900" },
    rose: { border: "border-rose-200 hover:border-rose-600", iconBg: "bg-rose-100 text-rose-700", accent: "text-rose-900" },
    success: { border: "border-emerald-200 hover:border-emerald-600", iconBg: "bg-emerald-100 text-emerald-700", accent: "text-emerald-900" },
    warning: { border: "border-amber-200 hover:border-amber-600", iconBg: "bg-amber-100 text-amber-800", accent: "text-amber-900" },
    slate: { border: "border-zinc-200 hover:border-zinc-900", iconBg: "bg-zinc-100 text-zinc-700", accent: "text-zinc-900" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className={`border ${t.border} bg-white p-4 rounded-sm transition-all hover:shadow-md hover:-translate-y-0.5`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{label}</div>
        {Icon && (
          <span className={`w-7 h-7 rounded-sm flex items-center justify-center ${t.iconBg}`}>
            <Icon className="w-3.5 h-3.5" strokeWidth={2} />
          </span>
        )}
      </div>
      <div className={`text-3xl font-semibold mt-2 tabular-nums ${t.accent}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, children, testId, right }) {
  return (
    <div className="border border-zinc-200 bg-white p-5 rounded-sm" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}


export default function Reports({ initialScope, lockScope = false } = {}) {
  const [scope, setScope] = useState(initialScope || "platform"); // "platform" | bu id
  const [bus, setBus] = useState([]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);

  const loadBUs = async () => {
    try { const { data } = await api.get("/business-units"); setBus(data); }
    catch (e) { /* silent */ }
  };
  const load = async () => {
    setData(null); setErr("");
    try {
      const path = scope === "platform" ? "/reports/platform" : `/reports/business-unit/${scope}`;
      const { data } = await api.get(path);
      setData(data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { loadBUs(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const download = async (kind /* "xlsx" | "csv" */) => {
    setDownloading(true);
    try {
      const path = scope === "platform"
        ? `/reports/platform/export.${kind}`
        : `/reports/business-unit/${scope}/export.xlsx`; // csv is platform-only
      const { data, headers } = await api.get(path, { responseType: "blob" });
      const filename = /filename="([^"]+)"/.exec(headers?.["content-disposition"] || "")?.[1]
        || `kpa-report.${kind}`;
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloading(false); }
  };

  const trendSeries = useMemo(() => (data?.reviews_trend || []).map((t) => ({
    ...t, avg_score: t.avg_score ?? 0,
  })), [data]);

  const quarterSeries = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.reviews_by_quarter).map(([q, s]) => ({
      quarter: q, total: s.total, completed: s.completed, avg_score: s.avg_score || 0, pct: s.completion_pct,
    }));
  }, [data]);

  const skillsSeries = useMemo(() => (data?.top_skills || []).slice(0, 10), [data]);
  const scoreHist = useMemo(() => (data?.score_histogram || []), [data]);
  const rolePie = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.counts.users_by_role || {}).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));
  }, [data]);

  return (
    <div data-testid="reports-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Reports</div>
        <h1 className="text-4xl font-bold text-zinc-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Analytics
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Roll-up of Business Units, users, reviews, policies and skills. Switch scope and export in one click.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-6 border-b border-zinc-200 pb-4">
        {!lockScope && (
          <>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mr-1">Scope:</span>
            <button
              data-testid="reports-scope-platform"
              onClick={() => setScope("platform")}
              className={`text-sm px-3 py-1.5 rounded-sm border ${scope === "platform" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}
            >
              Platform
            </button>
            {bus.map((b) => (
              <button
                key={b.id}
                data-testid={`reports-scope-${b.id}`}
                onClick={() => setScope(b.id)}
                className={`text-sm px-3 py-1.5 rounded-sm border ${scope === b.id ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}
              >
                {b.name}
              </button>
            ))}
          </>
        )}
        {lockScope && (
          <span className="text-xs text-zinc-500">
            Scope locked to this Business Unit.
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <button
            data-testid="reports-export-xlsx"
            disabled={downloading || !data}
            onClick={() => download("xlsx")}
            className="text-xs flex items-center gap-1 bg-emerald-600 text-white px-3 py-1.5 rounded-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> Export Excel
          </button>
          {scope === "platform" && (
            <button
              data-testid="reports-export-csv"
              disabled={downloading || !data}
              onClick={() => download("csv")}
              className="text-xs flex items-center gap-1 border border-zinc-300 text-zinc-800 px-3 py-1.5 rounded-sm hover:bg-zinc-50 disabled:opacity-50"
            >
              <Download className="w-3.5 h-3.5" /> Per-BU CSV
            </button>
          )}
        </div>
      </div>

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3">{err}</div>}
      {!data && !err && <div className="text-sm text-zinc-500">Loading report…</div>}

      {data && (
        <div className="space-y-6">
          {/* Headline KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPICard testId="report-kpi-bus" label="Business Units" value={data.counts.business_units} icon={Building2} tone="primary" />
            <KPICard testId="report-kpi-users" label="Users" value={data.counts.users} icon={Users} tone="violet"
              sub={Object.entries(data.counts.users_by_role || {}).map(([k, v]) => `${v} ${k.replace("_", " ")}`).join(" · ")} />
            <KPICard testId="report-kpi-reviews" label="Reviews completed" icon={TrendingUp}
              value={`${data.counts.reviews_completed}/${data.counts.reviews_total}`}
              sub={`${data.counts.reviews_total ? Math.round(100 * data.counts.reviews_completed / data.counts.reviews_total) : 0}% completion`}
              tone="emerald" />
            <KPICard testId="report-kpi-skills" label="Skills logged" value={data.counts.skills_total} icon={Star} tone="amber" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPICard testId="report-kpi-templates" label="Templates" value={data.counts.templates} icon={FileText} tone="amber" />
            <KPICard testId="report-kpi-profiles" label="Position profiles" value={data.counts.position_profiles} icon={ClipboardCheck} tone="emerald" />
            <KPICard testId="report-kpi-policies" label="Policies" icon={FileText} tone="primary"
              value={`${data.policies.approved}/${data.policies.total}`}
              sub={`${data.policies.pending} pending · ${data.policies.draft} draft`} />
            <KPICard testId="report-kpi-signoff" label="Sign-off progress" icon={ShieldCheck}
              value={`${data.signoff.pct}%`}
              sub={`${data.signoff.total_signed} of ${data.signoff.total_required} signatures`}
              tone="emerald" />
          </div>

          {/* Reviews trend */}
          <ChartCard testId="chart-trend" title="Reviews created vs completed (monthly)">
            {trendSeries.length === 0 ? (
              <div className="text-sm text-zinc-400 py-10 text-center">No reviews yet.</div>
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={trendSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="created" stroke="#2C7BB6" strokeWidth={2} />
                    <Line type="monotone" dataKey="completed" stroke="#10B981" strokeWidth={2} />
                    <Line type="monotone" dataKey="avg_score" stroke="#F59E0B" strokeWidth={2} strokeDasharray="4 2" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>

          {/* Quarter breakdown + score histogram */}
          <div className="grid md:grid-cols-2 gap-4">
            <ChartCard testId="chart-quarters" title="Reviews by quarter (completion %)">
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={quarterSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                    <XAxis dataKey="quarter" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="total" fill="#e4e4e7" name="Total" />
                    <Bar dataKey="completed" fill="#2C7BB6" name="Completed" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard testId="chart-scores" title="Final-score distribution (completed reviews)">
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={scoreHist}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>

          {/* Skills chart + Role pie */}
          <div className="grid md:grid-cols-2 gap-4">
            <ChartCard testId="chart-skills" title="Top skills across the organisation">
              {skillsSeries.length === 0 ? (
                <div className="text-sm text-zinc-400 py-10 text-center">No skills logged yet.</div>
              ) : (
                <div style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <BarChart data={skillsSeries} layout="vertical" margin={{ left: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                      <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={110} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8B5CF6" name="People" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </ChartCard>

            <ChartCard testId="chart-roles" title="Users by role">
              {rolePie.length === 0 ? (
                <div className="text-sm text-zinc-400 py-10 text-center">No users yet.</div>
              ) : (
                <div style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={rolePie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                        {rolePie.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </ChartCard>
          </div>

          {/* Average KPA score per BU — filterable (platform scope only) */}
          {scope === "platform" && <KpaByBUCard allBUs={bus} />}

          {/* Per-BU comparison table (platform scope only) */}
          {scope === "platform" && (
            <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid="report-per-bu-table">
              <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Per Business Unit</div>
                <Link to="/business-units" className="text-xs hover:underline">Open BU grid →</Link>
              </div>
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-zinc-50">
                    <tr>
                      {["Business Unit", "Users", "Profiles", "Templates", "Reviews", "Policies / SOPs", "Skills"].map((h) => (
                        <th key={h} className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-zinc-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_business_unit.map((r) => (
                      <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`report-burow-${r.id}`}>
                        <td className="px-4 py-2"><Link to={`/business-units/${r.id}`} className="hover:underline font-medium">{r.name}</Link></td>
                        <td className="px-4 py-2 tabular-nums">{r.users}</td>
                        <td className="px-4 py-2 tabular-nums">{r.position_profiles}</td>
                        <td className="px-4 py-2 tabular-nums">{r.templates}</td>
                        <td className="px-4 py-2 tabular-nums">
                          <span className="text-emerald-700">{r.reviews_completed}</span>
                          <span className="text-zinc-400">/{r.reviews_total}</span>
                          <span className="text-[11px] text-zinc-500 ml-1">({r.reviews_pct}%)</span>
                        </td>
                        <td className="px-4 py-2 tabular-nums">{r.policies_total} / {r.sops_total}</td>
                        <td className="px-4 py-2 tabular-nums">{r.skills_logged}</td>
                      </tr>
                    ))}
                    {data.per_business_unit.length === 0 && (
                      <tr><td colSpan={7} className="text-center text-sm text-zinc-400 py-8">No Business Units yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────
//  Average KPA score per Business Unit — filterable by range + BU.
//  Rendered inside Reports on Platform scope.
// ────────────────────────────────────────────────────────────
const PRESETS = [
  { id: "all", label: "All time" },
  { id: "ytd", label: "Year to date" },
  { id: "12m", label: "Last 12 months" },
  { id: "90d", label: "Last 90 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "custom", label: "Custom" },
];

function computeRange(preset) {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (preset === "all") return { from: "", to: "" };
  if (preset === "ytd") return { from: `${now.getFullYear()}-01-01`, to: iso(now) };
  if (preset === "12m") {
    const d = new Date(now); d.setMonth(d.getMonth() - 12); return { from: iso(d), to: iso(now) };
  }
  if (preset === "90d") {
    const d = new Date(now); d.setDate(d.getDate() - 90); return { from: iso(d), to: iso(now) };
  }
  if (preset === "30d") {
    const d = new Date(now); d.setDate(d.getDate() - 30); return { from: iso(d), to: iso(now) };
  }
  return { from: "", to: "" };
}

function KpaByBUCard({ allBUs }) {
  const [preset, setPreset] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selectedBUs, setSelectedBUs] = useState([]); // empty = all
  const [showBUPicker, setShowBUPicker] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // When preset changes (and not custom), refresh dates.
  useEffect(() => {
    if (preset === "custom") return;
    const { from: f, to: t } = computeRange(preset);
    setFrom(f); setTo(t);
  }, [preset]);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (selectedBUs.length > 0 && selectedBUs.length < allBUs.length) {
        params.set("bu_ids", selectedBUs.join(","));
      }
      const { data } = await api.get(`/reports/kpa-by-bu${params.toString() ? `?${params}` : ""}`);
      setData(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, selectedBUs.length]);

  const chartData = useMemo(() => (data?.rows || []).map((r) => ({
    name: r.bu_name,
    "Avg score": r.avg_score ?? 0,
    "Reviews": r.reviews_completed,
  })), [data]);

  const toggleBU = (id) => {
    setSelectedBUs((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  return (
    <div className="border border-zinc-200 bg-white rounded-sm" data-testid="kpa-by-bu-card">
      <div className="px-5 py-3 border-b border-zinc-200 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Average KPA score by Business Unit</div>
          <div className="text-xs text-zinc-500 mt-1">
            Real-time roll-up of completed reviews. Filter by time range and Business Units.
          </div>
        </div>
        <button data-testid="kpa-by-bu-refresh" onClick={load} disabled={loading}
          className="text-xs flex items-center gap-1 border border-zinc-300 px-2 py-1 rounded-sm hover:bg-zinc-50 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Filter row */}
      <div className="px-5 py-4 border-b border-zinc-200 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mr-1">Time:</span>
          {PRESETS.map((p) => (
            <button key={p.id} data-testid={`kpa-preset-${p.id}`}
              onClick={() => setPreset(p.id)}
              className={`text-xs px-2.5 py-1 rounded-sm border ${preset === p.id ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}>
              {p.label}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="kpa-custom-range">
            <span className="text-zinc-500">From</span>
            <input type="date" data-testid="kpa-date-from" value={from} onChange={(e) => setFrom(e.target.value)}
              className="border border-zinc-300 rounded-sm px-2 py-1 text-xs bg-white" />
            <span className="text-zinc-500">to</span>
            <input type="date" data-testid="kpa-date-to" value={to} onChange={(e) => setTo(e.target.value)}
              className="border border-zinc-300 rounded-sm px-2 py-1 text-xs bg-white" />
          </div>
        )}
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mr-1">Business Units:</span>
          <button data-testid="kpa-bu-all" onClick={() => setSelectedBUs([])}
            className={`text-xs px-2.5 py-1 rounded-sm border ${selectedBUs.length === 0 ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}>
            All ({allBUs.length})
          </button>
          <button data-testid="kpa-bu-picker" onClick={() => setShowBUPicker((v) => !v)}
            className="text-xs flex items-center gap-1 px-2.5 py-1 rounded-sm border border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400">
            <Filter className="w-3 h-3" /> {selectedBUs.length > 0 ? `${selectedBUs.length} selected` : "Pick BUs"}
          </button>
        </div>
        {showBUPicker && (
          <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50 flex flex-wrap gap-2 max-h-40 overflow-auto" data-testid="kpa-bu-list">
            {allBUs.map((b) => (
              <label key={b.id} className="text-xs flex items-center gap-1 bg-white border border-zinc-200 px-2 py-1 rounded-sm cursor-pointer">
                <input type="checkbox" data-testid={`kpa-bu-checkbox-${b.id}`}
                  checked={selectedBUs.includes(b.id)}
                  onChange={() => toggleBU(b.id)} />
                {b.name}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* KPIs + chart + table */}
      {err && <div className="p-4 text-sm text-red-700 bg-red-50 border-t border-red-200">{err}</div>}
      {!data && !err && <div className="p-6 text-sm text-zinc-500">Loading KPA breakdown…</div>}
      {data && (
        <div className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <KPICard testId="kpa-grand-avg" label="Overall avg score"
              value={data.grand_avg ?? "—"} sub="Weighted by all completed reviews in range" tone="success" icon={Star} />
            <KPICard testId="kpa-grand-count" label="Completed reviews"
              value={data.grand_reviews_completed} sub="Within the selected time range" />
            <KPICard testId="kpa-bu-count" label="Business Units in view"
              value={(data.rows || []).length} sub={selectedBUs.length > 0 ? "Filtered subset" : "All BUs"} />
          </div>

          {chartData.length === 0 ? (
            <div className="text-sm text-zinc-400 border border-dashed border-zinc-200 rounded-sm p-10 text-center">
              No Business Units in view. Adjust the filter above.
            </div>
          ) : (
            <div className="border border-zinc-100 rounded-sm p-3 mb-4">
              <div style={{ width: "100%", height: Math.max(260, chartData.length * 32) }}>
                <ResponsiveContainer>
                  <BarChart data={chartData} layout="vertical" margin={{ left: 12, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                    <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11 }} allowDecimals />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={150} />
                    <Tooltip formatter={(v, name) => name === "Avg score" ? [`${v}/5`, name] : [v, name]} />
                    <Bar dataKey="Avg score" fill="#2C7BB6" data-testid="kpa-bar" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {data.rows && data.rows.length > 0 && (
            <div className="overflow-auto border border-zinc-100 rounded-sm" data-testid="kpa-table">
              <table className="min-w-full text-sm">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Business Unit</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Reviews</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Avg score</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Q1</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Q2</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Q3</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Q4</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.bu_id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`kpa-row-${r.bu_id}`}>
                      <td className="px-3 py-2 font-medium">
                        <Link to={`/business-units/${r.bu_id}`} className="hover:underline">{r.bu_name}</Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.reviews_completed}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {r.avg_score !== null ? (
                          <b style={{ color: r.avg_score >= 4 ? "#059669" : r.avg_score >= 3 ? "#2C7BB6" : r.avg_score >= 2 ? "#F59E0B" : "#DC2626" }}>
                            {r.avg_score}
                          </b>
                        ) : <span className="text-zinc-300">—</span>}
                      </td>
                      {["Q1", "Q2", "Q3", "Q4"].map((q) => (
                        <td key={q} className="px-3 py-2 text-right tabular-nums text-xs">
                          {r.by_quarter[q].avg_score !== null
                            ? <>{r.by_quarter[q].avg_score}<span className="text-zinc-400"> ({r.by_quarter[q].reviews})</span></>
                            : <span className="text-zinc-300">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

